import { Avatar, Listy } from 'antd';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import type Grant from '../../../interfaces/Grant';
import { dateFromTicks, getUserInitials, getUserName } from '../../../utils/utils';
import ListyMetaRow from '../ListyMetaRow';
import { LISTY_ITEM_NO_HOVER } from '../listyItemClassNames';
import TruncatedTooltip from '../TruncatedTooltip';

export interface PendingGrantsListProps {
  grants: Grant[];
  formatTimestamp: (timestamp: string | Date) => string;
  onGrantClick: (grant: Grant) => void;
}

/**
 * List of pending share requests with scrollable content.
 * Used inside the Header pending grants Popover.
 */
export default function PendingGrantsList({
  grants,
  formatTimestamp,
  onGrantClick,
}: PendingGrantsListProps) {
  const { t } = useTranslation();

  return (
    <div className="flex max-h-[70vh] w-80 flex-col">
      <div className="border-gray-medium mb-3 shrink-0 border-b pb-2">
        <div className="flex items-center justify-between">
          <h3 className="text-blue-dark text-lg font-semibold">{t('common.shareRequests')}</h3>
        </div>
      </div>
      {grants.length === 0 ? (
        <div className="text-gray-dark py-8 text-center">
          {t('common.noShareRequests') || 'Nessuna richiesta di condivisione'}
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <Listy
            items={grants}
            rowKey="id"
            classNames={{
              item: clsx(
                LISTY_ITEM_NO_HOVER,
                'hover:bg-gray-light! cursor-pointer! rounded-md! px-0! py-3! transition-colors!',
              ),
            }}
            itemRender={(grant) => (
              <ListyMetaRow
                className="py-0"
                onClick={() => onGrantClick(grant)}
                avatar={
                  <Avatar size="large" className="bg-azure-hover! shrink-0!">
                    {getUserInitials(
                      grant.performedBy?.firstName,
                      grant.performedBy?.lastName,
                      grant.performedBy?.email,
                    )}
                  </Avatar>
                }
                title={
                  <div className="text-lg font-semibold">
                    <span className="text-gray-dark">From:</span>{' '}
                    {getUserName(
                      grant.performedBy?.firstName,
                      grant.performedBy?.lastName,
                      grant.performedBy?.email,
                    )}
                  </div>
                }
                description={
                  <div className="text-gray-dark flex max-w-80 flex-row items-center gap-1">
                    <span>{t('common.to')} </span>
                    <TruncatedTooltip className="min-w-0 flex-1">
                      <span className="font-semibold">
                        {grant.isGroup
                          ? grant.groupRecipient?.name
                          : getUserName(
                              grant.userRecipient?.firstName,
                              grant.userRecipient?.lastName,
                              grant.userRecipient?.email,
                            ) || '-'}
                      </span>
                    </TruncatedTooltip>
                    <span className="shrink-0">
                      {' '}
                      {formatTimestamp(dateFromTicks(grant.dateTimeRequest)) || '-'}
                    </span>
                  </div>
                }
              />
            )}
          />
        </div>
      )}
    </div>
  );
}
